import { useRef, useState } from 'react'
import type {
  OpticalMeasureFramingData,
  OpticalMeasureFramingDataEvent,
  OpticalMeasureNormalizedRect,
} from '../../index'

/**
 * Keeps the full latest native framing payload available without rerendering
 * at camera-frame frequency.
 *
 * The visible guide follows the native guide `revision`, which native bumps
 * only when the oval geometry actually changes — a new distance profile
 * (vbox on/off), a new target distance, or a new viewport. Everything else
 * arrives at frame rate and is dropped. Frames are also ignored while
 * calibration is `collecting`, so the oval swaps once the new geometry has
 * either locked or settled on its fallback rather than snapping to the
 * intermediate fallback first.
 *
 * The previous oval stays on screen throughout that recalibration: it is the
 * closest thing to correct while the new one resolves, and dropping it would
 * hide the guide for the whole calibration window.
 */
export function useMeasurementFramingGuide() {
  const latestFramingDataRef = useRef<OpticalMeasureFramingData | undefined>(
    undefined
  )
  const resolvedRevisionRef = useRef<number | undefined>(undefined)
  const [ovalFrame, setOvalFrame] = useState<OpticalMeasureNormalizedRect>()

  const handleFramingData = (event: OpticalMeasureFramingDataEvent) => {
    const incoming = event.nativeEvent
    latestFramingDataRef.current = incoming

    if (
      incoming.calibrationState === 'collecting' ||
      resolvedRevisionRef.current === incoming.revision
    ) {
      return
    }

    resolvedRevisionRef.current = incoming.revision
    setOvalFrame(incoming.oval)
  }

  return {
    ovalFrame,
    isReady: ovalFrame !== undefined,
    latestFramingDataRef,
    handleFramingData,
  }
}
